import { useLoad } from "@redotech/react-util/load";
import { StarRatingWidget } from "@redotech/redo-model/review/star-rating-widget-schema";
import { WidgetAutocheck } from "@redotech/redo-model/widget";
import { createContext, useContext } from "react";
import { RedoShopifyRpcClientContext } from "../client/redo-shopify-rpc-client";
import { log } from "../monitoring/log";

export const StarRatingsContext = createContext<
  | Record<string, { rating: number; numReviews: number } | null>
  | null
  | undefined
>(null);

export const StarRatingWidgetContext = createContext<
  StarRatingWidget | undefined | null
>(null);

export function StarRatingsProvider({
  children,
  productIds,
  autocheck,
}: {
  children: React.ReactNode;
  productIds: string[];
  autocheck: WidgetAutocheck;
}) {
  const rpcClient = useContext(RedoShopifyRpcClientContext);

  const ratings = useLoad(async () => {
    if (!rpcClient) return {};

    try {
      const uniqueProductIds = [...new Set(productIds)];
      log(`Fetching ratings for ${uniqueProductIds.length} products`);
      const result =
        await rpcClient.getProductsReviewsRatings(uniqueProductIds);
      return result;
    } catch (err) {
      log(`Error fetching ratings: ${err}`);
      return {};
    }
  }, [rpcClient, productIds]);

  const widgetSettings = useLoad(async () => {
    if (!rpcClient) return null;

    try {
      const teamId = autocheck.teamId;
      if (!teamId) {
        log("No team ID found in autocheck");
        return null;
      }
      log(`Fetching star rating widget settings for team ${teamId}`);

      const result = await rpcClient.getStarRatingWidgetByTeam({ teamId });
      return result;
    } catch (err) {
      log(`Error fetching star rating widget settings: ${err}`);
      return null;
    }
  }, [rpcClient, autocheck.teamId]);

  return (
    <StarRatingsContext.Provider value={ratings.value}>
      <StarRatingWidgetContext.Provider value={widgetSettings.value}>
        {children}
      </StarRatingWidgetContext.Provider>
    </StarRatingsContext.Provider>
  );
}
